/**
 * Unicode helpers. Never mutate original message text: these helpers only inspect it.
 */

const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

export function graphemes(text: string): string[] {
  return Array.from(segmenter.segment(text), (s) => s.segment);
}

const EMOJI_RE = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;
const KEYCAP_RE = /^[0-9#*]️?⃣$/u;

export function isEmojiGrapheme(g: string): boolean {
  return EMOJI_RE.test(g) || KEYCAP_RE.test(g);
}

/** A reaction must be exactly one emoji grapheme cluster (ZWJ sequences and flags included). */
export function isSingleEmoji(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) return false;
  const gs = graphemes(value);
  return gs.length === 1 && isEmojiGrapheme(gs[0]!);
}

/** All emoji grapheme clusters in order of appearance. */
export function extractEmojis(text: string): string[] {
  return graphemes(text).filter(isEmojiGrapheme);
}

/** Truncate to at most `max` graphemes without splitting emoji or surrogate pairs. */
export function truncateGraphemes(text: string, max: number): string {
  const gs = graphemes(text);
  if (gs.length <= max) return text;
  return gs.slice(0, max).join('') + '…';
}

/** Number of user-perceived characters. */
export function graphemeLength(text: string): number {
  return graphemes(text).length;
}

/** Message bodies: reject only things that are never legitimate (NUL, lone surrogates). */
export function isAcceptableMessageText(text: string): boolean {
  if (text.includes('\u0000')) return false;
  return text.isWellFormed();
}
